import { useQuery, useQueryClient } from "@tanstack/react-query";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { TopBar } from "@/components/chrome/TopBar";
import { ViewAsBanner } from "@/components/chrome/ViewAsBanner";
import { AccessGrid } from "@/components/admin/AccessGrid";
import { probeMe } from "@/lib/shell";
import { exitViewAs, rereadAfterViewAsChange, viewAsFromMe } from "@/lib/viewAs";

/**
 * S8 · The access-administration screen, at its own URL.
 *
 * A route rather than a modal because it is a screen people link each other to
 * ("look at row three") — and a view that cannot be linked to cannot be
 * discussed (UX §3, the same reason area/module/tab are all in the URL).
 *
 * Reaching it without the rights renders the refusal here rather than a blank:
 * the API is the control, and this page states what it said.
 */
export function AccessPage() {
  const queryClient = useQueryClient();
  const probe = useQuery({ queryKey: ["me"], queryFn: ({ signal }) => probeMe(signal), retry: false });
  const me = probe.data?.kind === "me" ? probe.data.me : null;
  const viewAs = viewAsFromMe(me ?? undefined);

  const leaveViewAs = async () => {
    await exitViewAs();
    await rereadAfterViewAsChange(queryClient);
  };

  return (
    <div className="flex min-h-dvh flex-col">
      <ViewAsBanner view={viewAs} onExit={() => void leaveViewAs()} />
      <TopBar me={me} canAdminister={me?.can_administer ?? false} />
      <main className="flex-1 p-8">
        {viewAs ? (
          // Reached while viewing as someone: say so rather than showing a grid
          // whose every action would be refused (UX F5).
          <Alert className="max-w-2xl">
            <AlertTitle>Access administration is read-only here</AlertTitle>
            <AlertDescription>Exit view-as to make a change.</AlertDescription>
          </Alert>
        ) : me ? (
          <AccessGrid
            actorEmail={me.person.email}
            canManagePeople={me.can_manage_people ?? false}
          />
        ) : null}
      </main>
    </div>
  );
}
